
import { useEffect, useState, useRef, useMemo } from "react";
import CityServices from "../services/CityServices";
import { Map, Source, Layer, Marker} from "@vis.gl/react-maplibre";
import { notifyError, notifySuccess } from "utils/toast";
import maplibreGl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

import { Input } from "@windmill/react-ui";

function CityMap() {
    const MAP_URL = process.env.REACT_APP_MAP_URL;
    const [cities, setCities] = useState([]);
    const [selectedCity, setSelectedCity] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [fenceCoordinates, setFenceCoordinates] = useState([]);
    const [history, setHistory] = useState([]); // for undo
    const [originalFence, setOriginalFence] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const [searchTerm, setSearchterm] = useState("");
    const [selectedPointIndex, setSelectedPointIndex] = useState(null);

    //quick search
    const filteredCities = cities.filter((city) =>
        city.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const mapRef = useRef(null);

    // Fetch cities
    useEffect(() => {
        const loadCities = async () => {
            try {
                const res = await CityServices.getCityFence();

                //  console.log("City fetch response:", res);

                //  FIXED condition
                if (res.status && res.data) {
                    //   console.log("Fetched cities:", res.data);
                    setCities(res.data);
                }
            } catch (err) {
                console.error(err);
            }
        };

        loadCities();
    }, []);
useEffect(() => {
    const handleKeyDown = (e) => {

        // UNDO (Ctrl + Z)
        if ((e.ctrlKey || e.metaKey) && e.key === "z") {
            e.preventDefault();
            if (history.length > 0) {
                const lastState = history[history.length - 1];
                setHistory((prev) => prev.slice(0, prev.length - 1));
                setFenceCoordinates(lastState);
            }
        }

        // DELETE selected point
        if (e.key === "Delete" && selectedPointIndex !== null) {
            e.preventDefault();

            const newCoords = fenceCoordinates.filter(
                (_, index) => index !== selectedPointIndex
            );

            updateFenceCoordinates(newCoords);
            setSelectedPointIndex(null);
        }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
}, [history, fenceCoordinates, selectedPointIndex]);



    // Load initial fence when city is selected
    useEffect(() => {
        if (!selectedCity?.fencing_geojson || !mapRef.current) return;

        const geo = selectedCity.fencing_geojson;
        let coords = [];

        if (geo.type === "FeatureCollection" && geo.features.length > 0) {
            const f = geo.features[0];
            coords =
                f.geometry.type === "Polygon"
                    ? f.geometry.coordinates[0]
                    : f.geometry.coordinates[0][0];
        } else if (geo.type === "Feature") {
            coords =
                geo.geometry.type === "Polygon"
                    ? geo.geometry.coordinates[0]
                    : geo.geometry.coordinates[0][0];
        } else if (geo.type === "Polygon") coords = geo.coordinates[0];
        else if (geo.type === "MultiPolygon") coords = geo.coordinates[0][0];

        const cloned = coords.map(c => [...c]);

        setFenceCoordinates(cloned);
        setOriginalFence(cloned);
        setIsDirty(false);

        // Fit bounds
        const lats = coords.map((c) => c[1]);
        const lngs = coords.map((c) => c[0]);
        const minLat = Math.min(...lats);
        const maxLat = Math.max(...lats);
        const minLng = Math.min(...lngs);
        const maxLng = Math.max(...lngs);

        if (mapRef.current && mapRef.current.fitBounds) {
            mapRef.current.fitBounds(
                [
                    [minLng, minLat],
                    [maxLng, maxLat],
                ],
                { padding: 50 }
            );
        }
    }, [selectedCity]);
    // const hasFenceChanged = () => {
    //   if (fenceCoordinates.length !== originalFence.length) return true;

    //   for (let i = 0; i < fenceCoordinates.length; i++) {
    //     if (
    //       fenceCoordinates[i][0] !== originalFence[i][0] ||
    //       fenceCoordinates[i][1] !== originalFence[i][1]
    //     ) {
    //       return true;
    //     }
    //   }
    //   return false;
    // };

    // Helper to update fence and push to history
    const updateFenceCoordinates = (newCoords) => {
        setHistory((prev) => [...prev, fenceCoordinates]); // save current state
        setFenceCoordinates(newCoords);
        setIsDirty(true); // THIS IS THE KEY
    };

    // Right-click to add a new fence point
  const handleRightClick = (event) => {
    if (isDragging) return; // 🔥 Prevent duplicate while dragging

    event.preventDefault();
    if (!event.lngLat) return;

    const newPoint = [event.lngLat.lng, event.lngLat.lat];
    updateFenceCoordinates([...fenceCoordinates, newPoint]);
};

    // Drag handler for points
    const handleDrag = (index, lngLat) => {
        const newCoords = [...fenceCoordinates];
        newCoords[index] = [lngLat.lng, lngLat.lat];
        updateFenceCoordinates(newCoords);
    };

    // Undo (Ctrl+Z)
    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === "z") {
                e.preventDefault();
                if (history.length > 0) {
                    const lastState = history[history.length - 1];
                    setHistory((prev) => prev.slice(0, prev.length - 1));
                    setFenceCoordinates(lastState);
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [history]);

    // --- Nearest neighbor ordering to avoid crossing lines ---
    const orderPointsNearestNeighbor = (points) => {
        if (points.length <= 2) return points;
        const ordered = [points[0]];
        const remaining = points.slice(1);

        while (remaining.length > 0) {
            const last = ordered[ordered.length - 1];
            let nearestIndex = 0;
            let minDist = distance(last, remaining[0]);

            for (let i = 1; i < remaining.length; i++) {
                const dist = distance(last, remaining[i]);
                if (dist < minDist) {
                    minDist = dist;
                    nearestIndex = i;
                }
            }

            ordered.push(remaining[nearestIndex]);
            remaining.splice(nearestIndex, 1);
        }
        return ordered;
    };

    const distance = (a, b) => {
        const dx = a[0] - b[0];
        const dy = a[1] - b[1];
        return dx * dx + dy * dy;
    };

    // Closed polygon coordinates (ordered)
    const closedFenceCoordinates = useMemo(() => {
        const ordered = orderPointsNearestNeighbor(fenceCoordinates);
        return ordered.length > 0 ? [...ordered, ordered[0]] : [];
    }, [fenceCoordinates]);

    // Fence GeoJSON
    const fenceGeoJSON = useMemo(
        () => ({
            type: "Feature",
            geometry: {
                type: "Polygon",
                coordinates: [closedFenceCoordinates],
            },
        }),
        [closedFenceCoordinates]
    );

    // Save fence to backend
    const handleSave = async () => {
        if (!selectedCity) {
            notifyError("Please select a city");
            return;
        }

        try {
            const response = await CityServices.updateCityFence(
                selectedCity.id_city,
                { fencing_geojson: fenceGeoJSON }
            );

            // Axios already returns parsed data
            const data = response;
            //console.log("Save fence response:", data);
            if (data.status) {
                notifySuccess(data.message || "Fence saved successfully");

                const updatedCity = {
                    ...selectedCity,
                    fencing_geojson: fenceGeoJSON,
                };

                // update selected city
                setSelectedCity(updatedCity);

                // update cities list
                setCities(prev =>
                    prev.map(city =>
                        city.id_city === selectedCity.id_city ? updatedCity : city
                    )
                );

                // reset dirty state
                setOriginalFence(fenceCoordinates);
                setIsDirty(false);

                // force MapLibre source refresh
                const map = mapRef.current;
                if (map?.getSource("city-fence")) {
                    map.getSource("city-fence").setData(fenceGeoJSON);
                }
            }

        } catch (err) {
            console.error(err);
            notifyError("Server error while saving fence");
        }
    };


    return (


        <div style={{ width: "100%", height: "100vh", display: "flex" }}>

            {/* City list */}
            <div
                style={{
                    width: "250px",
                    background: "#fff",
                    padding: "0px 10px",
                    overflowY: "auto",
                    boxShadow: "2px 0 5px rgba(0,0,0,0.1)",
                    zIndex: 1000,
                }}
            >

                <Input
                    className="border h-10 text-sm focus:outline-none block flex-1 bg-gray-100 border-transparent focus:bg-white sticky top-0"
                    type="search"
                    name="search"
                    placeholder="Quick Search"
                    autoComplete="off"
                    onChange={(e) => setSearchterm(e.target.value)}
                />

                <h3
                    className="mt-3 pt-3"
                    style={{ marginBottom: "12px", fontSize: "18px" }}
                >
                    Cities
                </h3>
                {filteredCities.map((city) => (
                    <div
                        key={city.id_city}
                        onClick={() => setSelectedCity(city)}
                        style={{
                            cursor: "pointer",
                            padding: "5px",
                            marginBottom: "3px",
                            borderRadius: "3px",
                            background:
                                selectedCity?.id_city === city.id_city ? "#0074D9" : "#f0f0f0",
                            color: selectedCity?.id_city === city.id_city ? "white" : "black",
                        }}
                    >
                        {city.name}
                    </div>
                ))}
            </div>

            {/* Map */}
            <div
                style={{
                    flex: 1,
                    position: "relative",
                    width: "80%",     //  reduce width
                    height: "100vh",  // full height
                    margin: "0 auto", // center it
                }}
            >
                {/* Save Button */}
                {isDirty && (
                    <button
                        onClick={handleSave}
                        style={{
                            position: "absolute",
                            top: "10px",
                            right: "10px",
                            zIndex: 2000,
                            padding: "8px 15px",
                            background: "#28a745",
                            color: "white",
                            border: "none",
                            borderRadius: "5px",
                            cursor: "pointer",
                        }}
                    >
                        Save Fence
                    </button>
                )}


                <Map
                    mapLib={maplibreGl}
                    initialViewState={{ latitude: 20, longitude: 78, zoom: 5 }}
                    style={{ width: "100%", height: "100%" }}
                    mapStyle={`${MAP_URL}/styles/basic/style.json`}
                    onLoad={(event) => (mapRef.current = event.target)}
                    onContextMenu={handleRightClick}
                >
                    {/* City center */}
                    {selectedCity && (
                        <Marker
                            latitude={selectedCity.latitude}
                            longitude={selectedCity.longitude}
                        />
                    )}

                    {/* Fence polygon */}
                    {closedFenceCoordinates.length >= 3 && (
                        <Source id="city-fence" type="geojson" data={fenceGeoJSON}>
                            <Layer
                                id="city-fill"
                                type="fill"
                                paint={{ "fill-color": "#FF5733", "fill-opacity": 0.2 }}
                            />
                            <Layer
                                id="city-line"
                                type="line"
                                paint={{ "line-color": "#FF5733", "line-width": 2 }}
                            />
                        </Source>
                    )}

                    {/* Draggable markers for fence points */}
                    {fenceCoordinates.map((coord, i) => (
              <Marker
  key={i}
  longitude={coord[0]}
  latitude={coord[1]}
  draggable={true}
  onClick={(e) => {
    e.originalEvent.stopPropagation();
    setSelectedPointIndex(i);
  }}
  onDragStart={() => setIsDragging(true)}
  onDragEnd={(event) => {
    handleDrag(i, event.lngLat);
    setIsDragging(false);
  }}
  style={{
    cursor: "pointer",
    filter:
      selectedPointIndex === i
        ? "drop-shadow(0 0 8px #00ffff)"
        : "none",
    transition: "filter 0.2s ease"
  }}
/>
                    ))}
                </Map>
            </div>
        </div>
    );
}

export default CityMap;